import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

export interface DiagnosticCheck {
  id: string;
  group: string;
  kind: "read" | "active";
}

export interface DiagnosticResult {
  status: "passed" | "failed" | "warning";
  message: string;
  durationMs: number;
  checkedAt: string;
  details?: Record<string, string | number>;
  audioBase64?: string;
}

interface DiagnosticState {
  checks: DiagnosticCheck[];
  results: Record<string, DiagnosticResult>;
  running: Record<string, boolean>;
  loading: boolean;
  error: string | null;
}

const initialState: DiagnosticState = {
  checks: [],
  results: {},
  running: {},
  loading: false,
  error: null,
};

export const fetchDiagnostics = createAsyncThunk<
  DiagnosticCheck[],
  void,
  { rejectValue: string }
>(
  "diagnostics/fetch",
  async (
    _,
    { rejectWithValue },
  ): Promise<DiagnosticCheck[] | ReturnType<typeof rejectWithValue>> => {
    try {
      const response = await adminApiClient.get<{ checks: DiagnosticCheck[] }>(
        "/admin/diagnostics",
      );
      return response.data.checks;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

export const executeDiagnostic = createAsyncThunk<
  { id: string; result: DiagnosticResult },
  { id: string; recipient?: string },
  { rejectValue: string }
>(
  "diagnostics/execute",
  async (
    { id, recipient },
    { rejectWithValue },
  ): Promise<
    | { id: string; result: DiagnosticResult }
    | ReturnType<typeof rejectWithValue>
  > => {
    try {
      const response = await adminApiClient.post<{ result: DiagnosticResult }>(
        `/admin/diagnostics/${encodeURIComponent(id)}`,
        { recipient },
        { timeout: 120000 },
      );
      return { id, result: response.data.result };
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

const diagnosticsSlice = createSlice({
  name: "diagnostics",
  initialState,
  reducers: {},
  extraReducers: (builder): void => {
    builder
      .addCase(fetchDiagnostics.pending, (state): void => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDiagnostics.fulfilled, (state, action): void => {
        state.loading = false;
        state.checks = action.payload;
      })
      .addCase(fetchDiagnostics.rejected, (state, action): void => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(executeDiagnostic.pending, (state, action): void => {
        state.running[action.meta.arg.id] = true;
        delete state.results[action.meta.arg.id];
        state.error = null;
      })
      .addCase(executeDiagnostic.fulfilled, (state, action): void => {
        state.running[action.payload.id] = false;
        state.results[action.payload.id] = action.payload.result;
      })
      .addCase(executeDiagnostic.rejected, (state, action): void => {
        state.running[action.meta.arg.id] = false;
        state.error = action.payload ?? null;
      });
  },
});

export default diagnosticsSlice.reducer;
